import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import PdfBodyBox from '../PdfComponents/PdfBodyBox';
import {
    paymentMethodTypeEnum,
    receiptDocumentPreviewNameByPaymentMethodType,
    voucherStatusesEnum
} from '../../../api/vouchers/helpers';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';

import PdfSection, { PdfRemarksSection } from '../PdfComponents/PdfSection';
import { VouchersPdfPropsType } from './voucherHelpers';

const ReceiptPdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            travelFileNumber,
            // travelFileOpenDate,
            // travelFileStartDate,
            remark,
            paymentMethodType,
            paymentMethodObject,
            coveredBy,
            isDeposited,
            isWithdrawn,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                passenger,
                agentInfo,
                supplierInfo,
                voucherCertification = 'Draft',
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                pdfBodyBoxData,
                bottomDetails,
                columns,
                rows,
                summaryLine,
                rateTableColumns,
                rateTableRows,
                rateLine,
                receivingBankText,
                cancelledByName,
                cancelledAtText,
                //topLeftDetailsText
            }
        },
        number: voucherNumber,
        type: documentType,
        status,
        createdAt
    } = props;

    // A cancelled receipt must never read as valid if it is printed or sent. The banner sits where
    // DEPOSITED / WITHDRAWN go and replaces them — they would print on top of each other.
    const isCancelled = status === voucherStatusesEnum.CANCEL;

    // Print what the document IS, never re-derive it from the payment method.
    // The two agree for anything issued under the current rule (bank transfer →
    // TRC, everything else → RCT), but they do NOT agree for the receipts issued
    // before it — e.g. card payments stored as 'Transfer Receipt'. Re-deriving
    // would reprint those under a heading that contradicts their stored type and
    // their document number.
    const documentTitle =
        documentType ||
        receiptDocumentPreviewNameByPaymentMethodType[paymentMethodType] ||
        'Receipt';

    return (
        <Document title={`${documentTitle} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {passenger ? <PdfPassengerDetails passenger={passenger} /> : <></>}
                                {agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : <></>}
                                {supplierInfo ? <PdfBillingContactDetails {...supplierInfo} /> : <></>}
                                {/* {topLeftDetailsText && <Text>For: {topLeftDetailsText}</Text>} */}
                            </View>

                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                travelFileNumber={travelFileNumber}
                                // travelFileOpenDate={travelFileOpenDate}
                                // travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={[styles.button]}>{voucherCertification}</Text>
                        {isDeposited && coveredBy?.number ? (
                            <Text style={{ ...styles.littleText }}>
                                {coveredBy.type} No. {coveredBy.number}
                            </Text>
                        ) : (
                            <></>
                        )}
                        {isWithdrawn && coveredBy?.number ? (
                            <Text style={{ ...styles.littleText }}>
                                {coveredBy.type} No. {coveredBy.number}
                            </Text>
                        ) : (
                            <></>
                        )}
                    </View>

                    <View style={[styles.section, styles.alignCenter]} wrap={false}>
                        {isCancelled ? (
                            <Text style={[styles.paymentStatus, styles.marginTopMinus24BottomMinus14]}>
                                {'CANCELLED'}
                            </Text>
                        ) : (
                            <></>
                        )}
                        {isDeposited && !isCancelled ? (
                            <Text style={[styles.paymentStatus, styles.marginTopMinus24BottomMinus14]}>
                                {'DEPOSITED'}
                            </Text>
                        ) : (
                            <></>
                        )}
                        {isWithdrawn && !isCancelled ? (
                            <Text style={[styles.paymentStatus, styles.marginTopMinus24BottomMinus14]}>
                                {'WITHDRAWN'}
                            </Text>
                        ) : (
                            <></>
                        )}
                        <PdfMainDetails
                            topDetails={`${documentTitle} No. ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel
                            mainDetailsTopStyle={styles.padding3_0}
                            mainDetailsBottomStyle={styles.padding2_0}
                        />
                    </View>

                    {pdfBodyBoxData?.length ? (
                        <View style={styles.section}>
                            <PdfBodyBox pdfBodyBoxData={pdfBodyBoxData} />
                        </View>
                    ) : (
                        <></>
                    )}

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent
                            columns={columns}
                            rows={rows}
                            summaryLine={summaryLine}
                            summaryLineStyle={styles.fontBoldAndBig1}
                            headerLineStyle={styles.fontBoldAndBig2}
                        />
                    </View>

                    {/* Rate line */}
                    {rateLine ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            {/* Body size, not littleText: on a shekel receipt this is the
                                "Equivalent: … USD" figure finance reads off the receipt. */}
                            <Text style={styles.fontBoldAndBig1}>{rateLine}</Text>
                        </View>
                    ) : rateTableRows?.length ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <PdfTableComponent
                                columns={rateTableColumns}
                                rows={rateTableRows}
                                tableStyle={styles.rateTableWidth}
                            />
                        </View>
                    ) : <></>}

                    {/* Details — Credit Card/Cash only */}
                    {paymentMethodObject?.details && (paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD || paymentMethodType === paymentMethodTypeEnum.CASH)
                        ? <PdfRemarksSection title="Details" body={paymentMethodObject.details} />
                        : <></>
                    }

                    {/* Receiving Bank — the account the money went into (TB's בנק יעד) */}
                    {receivingBankText &&
                    (paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER ||
                        paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD) ? (
                        <PdfSection
                            title="Receiving Bank:"
                            name={receivingBankText}
                            details={
                                paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER &&
                                paymentMethodObject?.reference
                                    ? [{ label: 'Bank Reference No.', value: paymentMethodObject.reference }]
                                    : undefined
                            }
                            labelWidth={115}
                        />
                    ) : <></>}

                    <PdfRemarksSection body={remark} />

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                    {isCancelled && (cancelledByName || cancelledAtText) ? (
                        <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#b42318', marginTop: 2, marginHorizontal: 10 }}>
                            {['Cancelled', cancelledByName && `by ${cancelledByName}`, cancelledAtText && `on ${cancelledAtText}`]
                                .filter(Boolean)
                                .join(' ')}
                        </Text>
                    ) : (
                        <></>
                    )}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(ReceiptPdf);
